import React, { useEffect, useState } from "react";
import styled from "styled-components";

import Container from "./container";
import { HyperLink } from "./../../simple-buttons/simple-buttons";

import { iControlProps } from "./../types";
import { Paragraph } from "./../../theme/typography";
import defaultTheme from "./../../theme/theme-test-override";
import { ThemeProps, ThemeTemplate } from "./../../theme/theme-template";

type PDFLinkTrackerProps = iControlProps & {
    pdfUrl: string;
    inputs?: { id: string; label: string; name: string }[];
    error?: string;
    iconUrl?: string;
    target?: string;
    disableTracking?: boolean;
};

const pdfIcon =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' xmlns:v='https://vecta.io/nano'%3E%3Cpath d='M5 4.5A2.5 2.5 0 0 1 7.5 2h5.473a1.88 1.88 0 0 1 1.328.549l5.148 5.15c.352.352.551.832.551 1.328V19.5c0 1.379-1.121 2.5-2.5 2.5h-10A2.5 2.5 0 0 1 5 19.5v-15zm14 14.7V9.146c0-.049-.004-.098-.007-.146h-4.424c-.816 0-1.477-.671-1.477-1.5V3.009A1.16 1.16 0 0 0 12.947 3H7.773C6.794 3 6 3.806 6 4.8v14.4c0 .994.794 1.8 1.773 1.8h9.455c.979 0 1.773-.806 1.773-1.8zM18.5 8c-.029-.036-4.496-4.473-4.5-4.5v3.625c0 .485.39.875.875.875H18.5z'/%3E%3C/svg%3E";
const successIcon =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='38' height='36' xmlns:v='https://vecta.io/nano'%3E%3Cpath d='M18 0a17.78 17.78 0 0 1 10 3.04 1 1 0 0 1 .28 1.38 1.02 1.02 0 0 1-1.4.28A16 16 0 0 0 6.029 28.634a16 16 0 0 0 20.992 2.564c6.546-4.482 8.805-13.068 5.361-20.174L17 26.42 8.3 17.7a.99.99 0 0 1 1.4-1.4l7.3 7.28L36.3 4.3a.99.99 0 0 1 1.4 1.4L33.845 9.56A1 1 0 0 1 34 9.78 18.24 18.24 0 0 1 36 18c0 9.941-8.059 18-18 18S0 27.941 0 18 8.059 0 18 0z' fill='%23161616'/%3E%3C/svg%3E";
const chevronRight =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='19' xmlns:v='https://vecta.io/nano'%3E%3Cpath d='M.719.121l-.29.29a.49.49 0 0 0 0 .696l8.018 8.018-8.018 8.018a.49.49 0 0 0 0 .696l.29.29a.49.49 0 0 0 .696 0l8.656-8.656a.49.49 0 0 0 0-.696L1.415.121a.49.49 0 0 0-.696 0z' fill='%23626466'/%3E%3C/svg%3E";

const StyledHyperLink = styled(HyperLink)<ThemeProps>`
    text-decoration: none;
    border: 1px solid ${props => props.theme.colors.primary};
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;

    &>div{
        width: 100%;
        margin: 0 1rem;
    }

    &::before {
        content: "";
        display: block;
        height: 40px;
        width: 48px;
        background-size: contain;
        background-image: url("${pdfIcon}");
        background-repeat: no-repeat;
        align-self: start;
    }

    &::after {
        content: url("${chevronRight}");
    }

    &.clicked{
        background-color: ${props => props.theme.colors.secondary};
        border-color: ${props => props.theme.colors.secondary};
        &::before{
            background-image: url("${successIcon}");
        }
    }
`;

StyledHyperLink.defaultProps = {
    icon: "",
    iconPosition: "",
    justifyContent: "",
    theme: {
        ...defaultTheme
    } as ThemeTemplate
};

const PDFLinkTracker = ({
    label,
    description,
    target = "_blank",
    onChange,
    disableTracking,
    ...props
}: PDFLinkTrackerProps) => {
    const { pdfUrl, inputs, isRequired, value } = props;
    const [isClicked, setIsClicked] = useState(
        !disableTracking && value && inputs && value[inputs[0].id] === "clicked"
    );
    const openInNewTab = (url: string, target: string) => {
        url && window.open(url, target, "noopener,noreferrer");
    };
    const triggerOnChange = () => {
        if (isRequired && !isClicked) {
            return;
        }

        inputs &&
            onChange &&
            onChange({
                [inputs[0].id]: `${isClicked ? "clicked" : "not clicked"}`
            });
    };
    useEffect(triggerOnChange, [isClicked]);

    return (
        <>
            <Container {...props}>
                <StyledHyperLink
                    icon=""
                    iconPosition=""
                    justifyContent="center"
                    className={`${
                        isClicked ? "clicked" : ""
                    } inputWrapper styledHyperLink`}
                    onClick={() => {
                        !disableTracking && setIsClicked(true);
                        openInNewTab(pdfUrl, target);
                    }}
                    px={15}
                    py={15}
                    target={target}
                >
                    <div>
                        <Paragraph mb={0} fontWeight="bold">
                            {label}
                        </Paragraph>
                        <Paragraph mb={0}>{description}</Paragraph>
                    </div>
                </StyledHyperLink>
            </Container>
        </>
    );
};

export default PDFLinkTracker;
